"use client";

import {
  CheckCircleIcon,
  SquarePenIcon,
  Trash2Icon,
  XCircleIcon,
} from "lucide-react";

import { SearchUsersQuery } from "@/hooks/queries/user/use-search-users-query";
import {
  UserFollowersAndSubscribers,
  UserSearchItem,
} from "@/services/users/schema";
import { useSession } from "next-auth/react";
import Image from "next/image";
import React, { PropsWithChildren } from "react";

import { Routes } from "@/api";
import { createChatAction } from "@/app/actions/chat/channel-actions";
import Loading from "@/components/loading";
import { Button, ButtonProps, buttonVariants } from "@/components/ui/button";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
} from "@/components/ui/command";
import { Intervals } from "@/constants";
import { DEFAULT_CHANNEL_PREFIX } from "@/controllers/channel/schema";
import { useIsDesktop } from "@/hooks/layout/use-is-desktop";
import { ChatChannelsQuery } from "@/hooks/queries/chat/use-channels-query";
import { MeChatsQuery } from "@/hooks/queries/user/use-me-chats-query";
import { useToggle } from "@/hooks/use-toggle";
import { cn } from "@/lib/utils";
import { useInboxSidebarStore } from "@/stores/sidebar-store";
import { generateRandomChatName } from "@/utils/chat/get-channel-name";
import { withStopPropagation } from "@/utils/with-stop-propagation";
import { ChannelType } from "@prisma/client";
import {
  redirect,
  RedirectType,
  usePathname,
  useRouter,
  useSearchParams,
} from "next/navigation";
import { toast } from "sonner";
import { useDebouncedCallback } from "use-debounce";
import { ChatInboxChannelFormSubmitButton } from "./chat-inbox-channel-form-submit-button";

interface Props {
  readonly user: UserFollowersAndSubscribers;
  readonly variant?: ButtonProps["variant"];
  readonly className?: string;
}

export function ChatInboxChannelForm(props: PropsWithChildren<Props>) {
  const openToggle = useToggle();
  const isDesktop = useIsDesktop();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Open the sidebar on desktop when the component mounts
  React.useEffect(() => {
    if (!isDesktop) {
      return;
    }
    if (!pathname?.startsWith(Routes.INBOX)) {
      return;
    }
    // Prevent opening the sidebar when the user is in a video chat
    if (searchParams?.get("type") === "video") {
      return;
    }

    useInboxSidebarStore.getState().update(true);
  }, [isDesktop, pathname, searchParams]);

  return (
    <>
      <Button
        variant={props.variant ?? "ghost"}
        className={cn(
          "max-w-sm text-muted-foreground hover:cursor-pointer hover:text-foreground md:px-2",
          props.className,
        )}
        onClick={openToggle.setOn}
      >
        {props.children ?? <SquarePenIcon className="size-4" />}
      </Button>
      <SearchUsers
        user={props.user}
        onOpenChange={openToggle.toggle}
        open={openToggle.value}
      />
    </>
  );
}

interface SearchUsersProps {
  readonly onOpenChange: (open: boolean) => void;
  readonly open: boolean;
  readonly user: UserFollowersAndSubscribers;
}

export function SearchUsers(props: SearchUsersProps) {
  const { data: session } = useSession();
  const router = useRouter();
  const [searchTerm, setSearchTerm] = React.useState("");
  const [chatMembers, setChatMembers] = React.useState<
    Map<string, UserSearchItem>
  >(new Map());
  const [channelType] = React.useState<ChannelType>(ChannelType.DIRECT);

  const searchUsersQuery = SearchUsersQuery.useQuery(searchTerm);
  const onSearchInputChange = useDebouncedCallback((value: string) => {
    setSearchTerm(value.toLowerCase());
  }, Intervals.SECOND / 2);

  const addUserToChannel = React.useCallback(
    (item: UserSearchItem) => {
      const newMembers = new Map(chatMembers);
      if (!newMembers.has(item.id)) {
        newMembers.set(item.id, item);
      } else {
        newMembers.delete(item.id);
      }

      setChatMembers(newMembers);
    },
    [chatMembers],
  );

  const removeUserFromChannel = React.useCallback(
    (item: UserSearchItem) => {
      const newMembers = new Map(chatMembers);
      newMembers.delete(item.id);

      setChatMembers(newMembers);
    },
    [chatMembers],
  );

  const createChat = React.useCallback(async () => {
    if (!session) {
      redirect(Routes.AUTHENTICATION, RedirectType.replace);
    }

    const memberIds = [
      session.user.id,
      ...Array.from(chatMembers.values()).map((member) => member.id),
    ];

    // If there are only two members, use the default channel name, otherwise generate a random one
    const name =
      memberIds.length === 2
        ? `${DEFAULT_CHANNEL_PREFIX}${memberIds.join(",")}`
        : generateRandomChatName();

    const result = await createChatAction(memberIds, name, channelType, {
      path: "/",
      tag: null,
    });
    if (!result.success) {
      toast.error(result.error);
      return;
    }

    toast.success("Channel created");
    MeChatsQuery.invalidate();
    ChatChannelsQuery.invalidate();

    props.onOpenChange(false);
    setChatMembers(new Map());

    if (result.channel) {
      router.push(Routes.INBOX_CHAT(result.channel.id));
    }
  }, [channelType, chatMembers, props, router, session]);

  // const selectFollowers = React.useCallback(() => {
  //   const newMembers = new Map(chatMembers);
  //   props.user.followers.forEach((follower) => {
  //     if (!newMembers.has(follower.id)) {
  //       newMembers.set(follower.id, follower);
  //     }
  //   });

  //   setChatMembers(newMembers);
  //   setChannelType(ChannelType.FOLLOWERS);
  // }, [chatMembers, props.user.followers]);

  // const selectSubscribers = React.useCallback(() => {
  //   const newMembers = new Map(chatMembers);
  //   props.user.subscribers.forEach((subscriber) => {
  //     if (!newMembers.has(subscriber.id)) {
  //       newMembers.set(subscriber.id, subscriber);
  //     }
  //   });

  //   setChatMembers(newMembers);
  //   setChannelType(ChannelType.SUBSCRIBERS);
  // }, [chatMembers, props.user.subscribers]);

  React.useEffect(
    function resetSearchTermOnOpen() {
      if (!props.open) {
        return;
      }
      setSearchTerm("");
    },
    [props.open],
  );

  if (!session) {
    return null;
  }

  return (
    <CommandDialog open={props.open} onOpenChange={props.onOpenChange}>
      <Command
        filter={(value, search) => {
          return value.includes(search.toLowerCase()) ||
            value.startsWith("member-")
            ? 1
            : 0;
        }}
      >
        <CommandInput
          placeholder="Search users"
          onValueChange={(value) => {
            onSearchInputChange(value);
          }}
        />
        {/* <div className="z-10 flex items-center gap-2 rounded-lg border-t bg-card p-2 py-2">
          <Button variant={"secondary"} size={"sm"} onClick={selectFollowers}>
            <UsersIcon className="mr-2 size-4" />
            Followers ({props.user.followers.length})
          </Button>
           <Button variant={"warning"} size={"sm"} onClick={selectSubscribers}>
            <CoinsIcon className="mr-2 size-4" />
            Subscribers ({props.user.subscribers.length})
          </Button> 
        </div> */}

        <div className="grid place-items-center"></div>
        {searchUsersQuery.isFetching ? (
          <CommandLoading>
            <div className="grid place-items-center p-4">
              <Loading size={64} description="Loading users..." />
            </div>
          </CommandLoading>
        ) : null}

        <CommandList>
          {searchUsersQuery.isSuccess && searchUsersQuery.data?.length === 0 ? (
            <CommandEmpty>No results found.</CommandEmpty>
          ) : null}
          {Boolean(searchUsersQuery.data?.length) && (
            <CommandGroup heading="Users">
              {searchUsersQuery.data?.map((user) => {
                if (chatMembers.has(user.id)) {
                  return null;
                }

                return (
                  <CommandItem
                    className="flex gap-2 hover:cursor-pointer"
                    key={user.id}
                    onSelect={() => {
                      addUserToChannel(user);
                    }}
                    value={user.name.toLowerCase()}
                    gtmEvent={{
                      event: "click",
                      eventCategory: "social",
                      eventLabel: "chat-search",
                      eventAction: "select",
                      userId: user.id ?? "",
                    }}
                  >
                    <Image
                      src={user.image ?? ""}
                      alt={user.name ?? "user name"}
                      width={32}
                      height={32}
                      className="rounded-full"
                    />
                    <span className="ml-2 font-semibold">{user.name}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}

          {chatMembers.size > 0 && (
            <CommandGroup
              heading={
                <p className="flex items-center">
                  Selected users
                  <Button
                    variant={"ghost"}
                    size={"icon"}
                    onClick={() => {
                      setChatMembers(new Map());
                    }}
                  >
                    <Trash2Icon className="size-4" />
                  </Button>
                </p>
              }
              className="pb-16"
            >
              {Array.from(chatMembers.values()).map((member) => (
                <CommandItem
                  className="flex gap-2 hover:cursor-pointer"
                  key={`selected-${member.id}`}
                  onSelect={() => {
                    addUserToChannel(member);
                  }}
                  value={`member-${member.id}`} // ensures items aren't filtered
                >
                  <Image
                    src={member.image ?? ""}
                    alt={member.name ?? "user name"}
                    width={32}
                    height={32}
                    className="rounded-full"
                  />
                  <span className="ml-2 font-semibold">{member.name}</span>
                  <div className="flex grow flex-row-reverse">
                    <Button
                      variant={"ghost"}
                      size={"icon"}
                      onClick={() => {
                        removeUserFromChannel(member);
                      }}
                      gtmEvent={{
                        event: "click",
                        eventCategory: "social",
                        eventLabel: "chat-channel-create-remove-member",
                        eventAction: "select",
                        userId: member.id ?? "",
                      }}
                    >
                      <XCircleIcon className="size-4 text-red-500" />
                    </Button>

                    <div
                      className={buttonVariants({
                        size: "icon",
                        variant: "ghost",
                      })}
                    >
                      <CheckCircleIcon className="size-4" />
                    </div>
                  </div>
                </CommandItem>
              ))}

              <div className="absolute bottom-0 left-0 right-0 z-10 flex items-center rounded-lg border-t bg-card p-2 py-2">
                <div className="flex-grow">
                  <span className="text-muted-foreground">
                    {chatMembers.size} selected
                  </span>
                </div>
                <form action={createChat} className="ml-auto space-x-2">
                  <Button
                    variant={"ghost"}
                    size={"sm"}
                    type="reset"
                    onClick={withStopPropagation(() => {
                      props.onOpenChange(false);
                    })}
                  >
                    Cancel
                  </Button>
                  <ChatInboxChannelFormSubmitButton size={"sm"} />
                </form>
              </div>

              <div className="grid place-items-center"></div>
            </CommandGroup>
          )}
        </CommandList>
      </Command>
    </CommandDialog>
  );
}
